/* eslint react-hooks/immutability: ["warn"] */
import {
  AlertResponse,
  Media,
  MediaResponse,
  RoomSettingsMedia,
} from "../types/media";
import styled from "styled-components";
import FixaImage from "@coworker/apprestructured/src/shared/wrappers/FixaImage/FixaImage";
import {
  fullMediaName,
  getMainAndAlternativeProducts,
  getMediaTypeText,
} from "../tools";
import { TaskLinks } from "../types/taskLink";
import { FixaSwitch } from "@coworker/apprestructured/src/shared/wrappers/FixaSwitch/FixaSwitch";
import { useTranslation } from "@coworker/locales";
import { MediaListItemCardDesktop } from "../styles/styles";
import FixaLink from "@coworker/apprestructured/src/shared/wrappers/FixaLink/FixaLink";
import { MediaTypes } from "../constants";

const ImageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 226px;
  overflow: hidden;

  .image--fallback {
    background-color: var(--grey100);
  }
`;

const StyledImage = styled(FixaImage)<{ rotation: number }>`
  object-fit: cover;
  object-position: center center;
  transform: rotate(${({ rotation }) => rotation}deg);
  height: 100%;
  width: 100%;
`;

const ImageAndNameContainer = styled.div`
  position: relative;
  width: 100%;
  height: 262px;
`;

const NameText = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: black;
  line-height: 24px;
  margin-top: 12px;
  white-space: nowrap;
  overflow: hidden;
`;

const ArticleInfoBarContainer = styled.div`
  display: block;
  float: left;
  width: 100%;
  height: 22px;
  margin-top: 8px;
`;

const ArticleInfoTextBox = styled.div<{
  $color: string;
  $backgroundColor: string;
}>`
  display: block;
  float: left;
  color: ${({ $color }) => $color};
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  padding: 2px 8px;
`;

const MediaTypeText = styled.div`
  position: absolute;
  left: 5px;
  top: 5px;
  font-size: 12px;
  font-weight: 400;
  color: #484848;
  background-color: #f5f5f5;
  line-height: 18px;
  padding: 2px 8px;
`;

const MediaBarContainer = styled.div`
  display: flex;
  float: left;
  margin-top: 25px;
  justify-content: space-between;
  width: 100%;
  height: 44px;
`;

const MediaInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const ArticleCountText = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #484848;
  line-height: 22px;
`;

const StyleGroupText = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: #484848;
  line-height: 22px;
`;

const SwitchContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ActiveLabel = styled.label`
  font-size: 12px;
  font-weight: 400;
  color: #484848;
  line-height: 12px;
  margin-bottom: 4px;
`;

interface MediaListItemDesktopProps {
  mediaResponse: MediaResponse;
  alert: AlertResponse | undefined;
  tasks: TaskLinks | undefined;
  onSaveMedia: (media: Media) => void;
}

export function MediaListItemDesktop({
  mediaResponse,
  alert,
  tasks,
  onSaveMedia,
}: MediaListItemDesktopProps) {
  const { t } = useTranslation();

  const onActiveChange = () => {
    mediaResponse.media.active =
      mediaResponse.media.active === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    onSaveMedia(mediaResponse.media);
  };

  const mainAndAlternativeProduct = getMainAndAlternativeProducts(
    mediaResponse.media
  );
  return (
    <MediaListItemCardDesktop
      data-testid={
        "mediaListItemCardDesktop:" +
        fullMediaName(
          mediaResponse.media.name,
          mainAndAlternativeProduct.mainProductName1,
          mainAndAlternativeProduct.mainProductName2
        )
      }
    >
      <FixaLink
        to={`/mediamaintenance/tabs/${mediaResponse.media.id}?tab=article-list-tab`}
      >
        <ImageAndNameContainer>
          <ImageContainer>
            {!mediaResponse.media.mainImageUrl ? (
              <StyledImage
                src={""}
                alt="No image"
                rotation={0}
                fallback={true}
              />
            ) : (
              <StyledImage
                src={mediaResponse.media.mainImageUrl}
                alt=""
                rotation={mediaResponse.media.mainImageRotation ?? 0}
                loading="eager"
              />
            )}
          </ImageContainer>
          <NameText>
            {fullMediaName(
              mediaResponse.media.name,
              mainAndAlternativeProduct.mainProductName1,
              mainAndAlternativeProduct.mainProductName2
            )}
          </NameText>
          <MediaTypeText>
            {getMediaTypeText(mediaResponse.media.mediaType as MediaTypes)}
          </MediaTypeText>
        </ImageAndNameContainer>
        <ArticleInfoBarContainer>
          {alert !== undefined && alert.salesStop > 0 && (
            <ArticleInfoTextBox
              $color="#E00751"
              $backgroundColor="#FCE6EE"
            >{`${alert.salesStop} Sale stop`}</ArticleInfoTextBox>
          )}
          {alert !== undefined && alert.eds > 0 && (
            <ArticleInfoTextBox
              $color="#CA5008"
              $backgroundColor="#F2BEA7"
            >{`${alert.eds} EDS`}</ArticleInfoTextBox>
          )}
          {tasks !== undefined && tasks.length > 0 && (
            <ArticleInfoTextBox
              $color="#0A8A00"
              $backgroundColor="#E6F3E5"
            >{`${tasks.length} Tasks`}</ArticleInfoTextBox>
          )}
        </ArticleInfoBarContainer>
        <MediaBarContainer>
          <MediaInfo>
            <ArticleCountText>
              {mediaResponse.media.mediaType === MediaTypes.HOME
                ? t("countRoomsString", {
                    count: mediaResponse.children?.filter(
                      (c) => c.parentMediaId === mediaResponse.media.id
                    ).length,
                  })
                : t("countArticlesString", {
                    count: parseInt(`${mediaResponse.media?.nbrArticles ?? 0}`),
                  })}
            </ArticleCountText>
            <StyleGroupText>
              {(mediaResponse.media as RoomSettingsMedia)?.styleGroup}
            </StyleGroupText>
          </MediaInfo>
          <SwitchContainer>
            <ActiveLabel htmlFor="active">
              {mediaResponse.media.active === "ACTIVE"
                ? t("activeString")
                : t("inactiveString")}
            </ActiveLabel>
            <FixaSwitch
              id={"active"}
              checked={mediaResponse.media.active === "ACTIVE"}
              value={mediaResponse.media.active}
              onChange={onActiveChange}
              disabled={!mediaResponse.media.name}
              onClick={(event) => {
                event.stopPropagation();
              }}
            />
          </SwitchContainer>
        </MediaBarContainer>
      </FixaLink>
    </MediaListItemCardDesktop>
  );
}
